import React, { useState, useEffect } from 'react';
import { Users, Target, Award, Rocket, Code, Bot, Zap, Shield, ChevronUp, Calendar } from 'lucide-react';
import Footer from '../components/Footer';
import ScrollIndicator from '../components/ScrollIndicator';

const About = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setShowScrollTop(scrollTop > 800);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="h-screen overflow-y-scroll snap-y snap-mandatory scroll-container">
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white min-h-[90vh] snap-start flex flex-col justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-slate-900/0"></div>
        <div className="container-width relative flex-1 flex flex-col justify-center items-center py-16 sm:py-20">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-200">About ViSRUPT</h1>
            <div className="space-y-4">
              <p className="text-base sm:text-lg text-slate-300">
                At Visrupt, we specialize in crafting high-performance, custom software solutions that optimize trading, automate workflows, and enhance digital interactions.
              </p>
              <p className="text-base sm:text-lg text-slate-300">
                We believe in precision, efficiency, and confidentiality. Every project we take on is tailored to meet our clients' unique needs while ensuring complete security through NDAs and formal acknowledgments, giving you full ownership of the solutions we build.
              </p>
              <p className="text-base sm:text-lg text-slate-300">
                Our approach is simple—we understand, innovate, and automate. Whether you need a robust trading algorithm, an advanced chatbot, or seamless process automation, Visrupt ensures smooth execution with reliable performance.
              </p>
            </div>
          </div>
        </div>
        <ScrollIndicator text="Discover Our Story" />
      </section>

      {/* Why Choose Us */}
      <section className="min-h-screen snap-start bg-white flex items-center py-24">
        <div className="container-width">
          <h2 className="text-2xl sm:text-3xl font-bold mb-12 text-center">Why Choose ViSRUPT?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Target className="w-8 h-8" />,
                title: "Tailored Solutions",
                description: "Every business operates differently, so we don't do one-size-fits-all. Our solutions are custom-built, ensuring they integrate seamlessly with your workflow. If it doesn't fit, we refine it until it does."
              },
              {
                icon: <Award className="w-8 h-8" />,
                title: "Expertise Across Domains",
                description: "From algorithmic trading to automation and web solutions, we bring deep expertise to every project. We don't just code—we engineer solutions that actually make a difference."
              },
              {
                icon: <Rocket className="w-8 h-8" />,
                title: "Efficiency and Automation",
                description: "Time is money, and manual work is a waste of both. We help businesses optimize operations, streamline workflows, and reduce human error with smart automation that does the heavy lifting."
              },
              {
                icon: <Users className="w-8 h-8" />,
                title: "Client-Centric Approach",
                description: "Your goals are our priority. We work closely with you to understand your needs and deliver solutions that drive real value. If it doesn't benefit you, it's not worth building."
              },
              {
                icon: <Shield className="w-8 h-8" />,
                title: "Security and Transparency",
                description: "For every project, confidentiality is non-negotiable. NDAs, clear ownership agreements, and secure processes ensure that your proprietary strategies remain yours—always."
              },
              {
                icon: <Code className="w-8 h-8" />,
                title: "Long-Term Partnerships",
                description: "We don't do one-off gigs and vanish. Our focus is on long-term collaboration, helping you scale and evolve with technology while keeping your competitive edge sharp."
              }
            ].map((feature, index) => (
              <div key={index} className="bg-slate-50 p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className="text-blue-500 mb-4 flex justify-center sm:justify-start">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-4 text-slate-800 text-center sm:text-left">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Divisions Section */}
      <section className="min-h-screen snap-start bg-slate-50 flex items-center py-24">
        <div className="container-width">
          <h2 className="text-2xl sm:text-3xl font-bold mb-12 text-center">Our Divisions</h2>
          
          {/* ViSiON */}
          <div className="bg-white rounded-xl shadow-lg p-8 sm:p-10 mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-0 mb-6">
              <div className="bg-blue-50 p-4 rounded-xl">
                <Zap className="w-12 h-12 text-blue-500" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-slate-800 sm:ml-6">ViSiON – Algorithmic Trading & Backtesting Solutions</h3>
            </div>
            <div className="space-y-4 text-slate-600">
              <p className="leading-relaxed">
                ViSiON is built for traders who demand precision, speed, and execution without compromise. In a market where milliseconds matter, we develop custom trading algorithms that remove human error and optimize opportunities, ensuring your strategies perform at peak efficiency.
              </p>
              <p className="leading-relaxed">
                Beyond just development, we provide comprehensive backtesting services, allowing traders to refine and validate strategies against historical data before risking capital. Whether you're an independent trader or a business managing institutional-level execution, ViSiON ensures your strategies are rigorously tested and built for real-world performance.
              </p>
              <p className="leading-relaxed">
                To maintain complete confidentiality, every project is protected under NDAs and formal ownership agreements, ensuring that your trading strategies remain exclusively yours.
              </p>
            </div>
          </div>

          {/* ViSFLOW */}
          <div className="bg-white rounded-xl shadow-lg p-8 sm:p-10">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-0 mb-6">
              <div className="bg-blue-50 p-4 rounded-xl">
                <Bot className="w-12 h-12 text-blue-500" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-slate-800 sm:ml-6">ViSFLOW – Process Automation & Chatbot Development</h3>
            </div>
            <div className="space-y-4 text-slate-600">
              <p className="leading-relaxed">
                ViSFLOW is built to eliminate inefficiencies, automate repetitive tasks, and seamlessly integrate systems so you can focus on what actually matters. Whether it's replacing tedious manual work with Python automation, streamlining data processing, or building APIs that fit your business instead of forcing you into generic solutions, we make technology work for you—not the other way around.
              </p>
              <p className="leading-relaxed">
                Beyond automation, ViSFLOW brings intelligence to interactions. Our Telegram and Discord bots handle support, engagement, and real-time responses without the need for constant oversight. AI-driven automation ensures seamless customer interactions, database integrations, and workflow optimizations that go beyond answering basic queries. For those in the gaming and interactive space, we develop Discord game bots that enhance engagement, automate in-game functionalities, and keep communities active—without requiring manual intervention. Every feature is custom-built, ensuring flexibility and efficiency tailored to your needs.
              </p>
              <p className="leading-relaxed">
                ViSFLOW ensures your business operates faster, smarter, and with less manual effort. If a task is repetitive, we automate it—simple as that.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story & Commitment */}
      <section className="min-h-screen snap-start bg-white flex items-center py-24">
        <div className="container-width">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Our Story */}
            <div className="bg-slate-50 rounded-xl p-8 sm:p-10">
              <h2 className="text-2xl sm:text-3xl font-bold mb-6">Our Story</h2>
              <div className="space-y-4 text-slate-600 text-base leading-relaxed">
                <p>
                  ViSRUPT started in 2019 with a simple goal—eliminate inefficiency. We built ViSFLOW to help businesses cut out repetitive tasks, automate workflows, and stop wasting hours on things a script could do in seconds. Python automation, chatbots, API integrations—you name it, we streamlined it.
                </p>
                <p>
                  Then came March 2020—markets crashed, uncertainty spiked, and manual trading turned into a full-time anxiety sport. While traders were glued to their screens, trying to time entries and exits, we saw a problem: human emotions were losing to cold, hard algorithms.
                  That's when we expanded into algorithmic trading with ViSiON. No second-guessing, no hesitation—just custom-built strategies that executed trades with precision while everyone else was stuck in indecision. The demand for smarter, faster execution skyrocketed, and we stepped in to build high-performance trading systems that could handle the volatility without breaking a sweat.
                </p>
                <p>
                  Since then, ViSRUPT has been on a relentless pursuit of automation and optimization. Whether it's trading algorithms, workflow automation, or chatbots, our mission remains the same: work smarter, not harder.
                </p>
              </div>
            </div>

            {/* Our Commitment */}
            <div className="bg-slate-50 rounded-xl p-8 sm:p-10">
              <h2 className="text-2xl sm:text-3xl font-bold mb-6">Our Commitment</h2>
              <div className="space-y-4 text-slate-600 text-base leading-relaxed">
                <p>
                  We are committed to providing solutions that deliver real, measurable results. At ViSRUPT, we understand that technology is not just about building systems—it's about enabling businesses to perform at their best.
                </p>
                <p>
                  With ViSRUPT, you're not just getting a service—you're gaining a dedicated partner who cares about your goals and is invested in your success. Let us handle the technical complexities, so you can focus on what you do best.
                </p>
                <div className="mt-8 space-y-4">
                  <p>Contact us at: <a href="mailto:contact@visrupt.in" className="text-blue-500 hover:text-blue-600 transition">contact@visrupt.in</a></p>
                  <p>Call us: <a href="tel:+917522979568" className="text-blue-500 hover:text-blue-600 transition">+91-7522979568</a></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="snap-start">
        <div className="bg-blue-500 text-white py-24">
          <div className="container-width text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Get Started?</h2>
            <p className="text-xl mb-8">Contact us to discuss your project requirements</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <div className="space-y-4">
                <p className="text-lg">Email: <a href="mailto:contact@visrupt.in" className="underline">contact@visrupt.in</a></p>
                <p className="text-lg">Phone: <a href="tel:+917522979568" className="underline">+91-7522979568</a></p>
              </div>
              <div className="sm:ml-8">
                <a
                  href="https://calendly.com/visrupt/schedule-a-call"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 bg-white text-blue-500 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-300 group"
                >
                  <Calendar className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-300" />
                  <span className="group-hover:translate-x-1 transition-transform duration-300">Schedule a Call</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </section>

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 bg-blue-500 text-white p-3 rounded-full shadow-lg transition-all duration-300 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${
          showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'
        }`}
        aria-label="Scroll to top"
      >
        <ChevronUp className="w-6 h-6" />
      </button>
    </div>
  );
};

export default About;